import { $applyNodeReplacement, DecoratorNode, EditorConfig, LexicalEditor, SerializedLexicalNode, Spread } from "lexical";
import { ContainerMixin, ContainerMixinPayload, ContainerPayload, SerializedContainer } from "../mixins/ContainerMixin";
import { ImageMixin, ImageMixinPayload, ImagePayload, SerializedImage } from "../mixins/ImageMixin";
import React, { Suspense } from "react";
import { InlineMixin } from "../mixins/InlineMixin";

const InlineComponent = React.lazy(() => import('../mixins/components/InlineComponent'));
const ImageComponent = React.lazy(() => import('../mixins/components/ImageComponent'));

export interface InlineImagePayload extends ContainerPayload, ImagePayload {
  key?: string;
}

export type SerializedInlineImageNode = Spread<
  SerializedContainer & SerializedImage,
  SerializedLexicalNode
>;

class InlineImageBase extends DecoratorNode<JSX.Element> implements ContainerMixinPayload, ImageMixinPayload {
  __src!: string;
  __altText!: string;
  __width!: number;
  __height!: number;

  constructor(
    src: string,
    altText: string,
    width?: number,
    height?: number,
    key?: string,
  ) {
    super(key);
    this.__width = width || 0;
    this.__height = height || 0;
    this.__src = src || '';
    this.__altText = altText || '';
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const element = document.createElement('span');
    element.className = 'inline-image';
    return element;
  }

  updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: EditorConfig): boolean {
    return false;
  }

  exportJSON(): SerializedInlineImageNode {
    const self = this.getLatest();
    return {
      type: InlineImageNode.getType(),
      version: 1,
      src: self.__src,
      altText: self.__altText,
      width: self.__width,
      height: self.__height,
    };
  }
}

export class InlineImageNode extends ImageMixin(InlineMixin( ContainerMixin(InlineImageBase))) {
  static getType() {
    return "inline-image";
  }

  static clone(node: InlineImageNode) {
    return new InlineImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key);
  }

  static importJSON(json: SerializedInlineImageNode) {
    const { src, altText, width, height } = json;
    const node = $createInlineImageNode(src, altText, width, height);
    return node;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <InlineComponent
          nodeKey={this.__key}
        >
          <ImageComponent
            nodeKey={this.__key}
            src={this.__src}
            altText={this.__altText}
          />
        </InlineComponent>
      </Suspense>
    )
  }
}

export function $createInlineImageNode(
  src: string,
  altText: string,
  width?: number,
  height?: number,
  key?: string,
) {
  return $applyNodeReplacement(new InlineImageNode(src, altText, width, height, key));
}

export function $isInlineImageNode(node: unknown): node is InlineImageNode {
  return node instanceof InlineImageNode;
}